import { fileDownloadConfig, httpClient } from '@/httpClient';
import { PlanType } from '@/enums/planType';
import qs from 'qs';
import config from '@/config'; // eslint-disable-line import/extensions

const getCrmPlanById = (id, requestOptions) => httpClient.get(`/api/crm/plans/${id}/`, requestOptions);

const overridePlan = ({ crmPlanId, planId }) => httpClient
  .post(`/api/crm/plans/${crmPlanId}/link_service_plan`, { plan_id: planId });

const getCrmPlan3dById = (id, requestOptions) => httpClient.get(`/api/crm/plans/${id}/plan_3d`, requestOptions);

const getCrmPlanState = (id) => httpClient.get(`/api/crm/plans/${id}/state`, {
  silent: true,
  timeout: 10000,
});

const getToWorkAvailableCount = async ({ type } = {}) => httpClient
  .get('/api/crm/plans/get_to_work_available_count', { params: { type } });

// Без type берётся лучшая доступная планировка любого типа — для единого списка панели ниндзя.
const takePlanToWork = ({ type } = {}) => httpClient
  .post('/api/crm/plans/get_oldest_to_work', type === undefined || type === null ? {} : { type });

// Пока у ниндзя есть отклонённые планировки, бэкенд не выдаёт новые — тем же условием
// гасим кнопку взятия в работу.
const getNinjaHasRejectedPlans = () => httpClient.get('/api/crm/plans/ninja_has_rejected');

const getNinjaStatisticById = async (id, { fromDate, toDate }) => httpClient.get(
  `/api/crm/statistic/ninja/${id}`,
  { params: { from_date: fromDate, to_date: toDate } },
);

const getNinjaStatistic3dById = async (id, { fromDate, toDate }) => httpClient.get(
  `/api/crm/statistic/ninja_3d/${id}`,
  { params: { from_date: fromDate, to_date: toDate } },
);

const getServicePlanList = ({ page = 1, filter }) => httpClient.get('/api/plans', {
  params: {
    page,
    filter,
    sort: 'created_at',
  },
  paramsSerializer: (s) => qs.stringify(s),
});

const getServicePlanById = async (id) => httpClient.get(`/api/plans/${id}`);
const getServicePlanArchiveStatus = (id, params = {}) => httpClient.get(`/api/plans/${id}/archive`, {
  params,
  paramsSerializer: (s) => qs.stringify(s, { arrayFormat: 'brackets' }),
});
const startServicePlanArchive = (id, params = {}) => httpClient.post(`/api/plans/${id}/archive`, null, {
  params,
  paramsSerializer: (s) => qs.stringify(s, { arrayFormat: 'brackets' }),
});
const updateServicePlanById = ({ id, payload }) => httpClient.patch(
  `/api/plans/${id}`,
  payload,
  { headers: { 'Content-Type': 'application/json' } },
);
const changePlanUnrealImage = ({ id, formData, headers }) => httpClient
  .post(`/api/plans/${id}/change_image`, formData, headers);

const previewPlanImageReplacements = (id, formData) => httpClient
  .post(`/api/plans/${id}/image_replacements/preview`, formData, { silent: true });
const replacePlanImages = (id, formData) => httpClient
  .post(`/api/plans/${id}/image_replacements`, formData, { silent: true });

const getTagsList = () => httpClient.get('/api/crm/tags');
const createTag = (request) => httpClient.post('/api/crm/tags', request);
const editTag = ({ id, name }) => httpClient.post(`/api/crm/tags/${id}`, { name });
const deleteTag = (id) => httpClient.delete(`/api/crm/tags/${id}`);

const getUsersList = ({
  page, filter, sort, withTrashed,
}) => httpClient.get('/api/users', {
  params: {
    page,
    filter,
    sort,
    with_trashed: withTrashed ? 1 : undefined,
  },
  paramsSerializer: (s) => qs.stringify(s),
});
const getDeletedUsersList = ({ page, filter, sort }) => httpClient.get('/api/users/deleted', {
  params: {
    page,
    filter,
    sort,
  },
  paramsSerializer: (s) => qs.stringify(s),
});
const changeUserRole = (request) => httpClient.post('/api/users/change_role', request);
const changeUserName = ({ id, name }) => httpClient.patch(`/api/users/${id}/rename`, { name });
const deleteUser = (id) => httpClient.delete(`/api/users/${id}`);
const restoreUser = (id) => httpClient.patch(`/api/users/${id}/restore`);
const changeNinjaStatus = (id, ninjaExpertLevel2d, ninjaExpertLevel3d) => httpClient
  .post(
    `/api/users/${id}/change_ninja_expert_level`,
    {
      ninja_expert_level_2d: ninjaExpertLevel2d,
      ninja_expert_level_3d: ninjaExpertLevel3d,
    },
  );

const changeBetaTesterStatus = (id, betaTester) => httpClient
  .post(`api/users/${id}/change_beta_tester_status`, { beta_tester: betaTester });

const changeDatasetVoterStatus = (id, datasetVoter) => httpClient
  .post(`api/users/${id}/change_dataset_voter_status`, { dataset_voter: datasetVoter });

const changeCanGetPlansToWorkStatus = (id, value) => httpClient
  .post(`api/users/${id}/change_can_get_plans_to_work_status`, { can_get_plans_to_work: value });

const changePlansLimit = (id, value) => httpClient
  .post(`api/users/${id}/plans_limit`, { plans_limit: value });

const changeReviewerPlansLimit = (id, value) => httpClient
  .post(`api/users/${id}/reviewer_plans_limit`, { reviewer_plans_limit: value });

const executeCrmPlanAction = ({ planId, action }) => httpClient.post(`/api/crm/plans/${planId}/${action}`);
const reviewCrmPlan = (requestData) => {
  const { planId, ...dataToSend } = requestData;
  return httpClient.post(`/api/crm/plans/${planId}/review`, dataToSend);
};
const reviewPlan = (requestData) => {
  const { planId, ...dataToSend } = requestData;
  return httpClient.post(`/api/plans/${planId}/review`, dataToSend);
};
const deleteCrmPlan = (id) => httpClient.delete(`/api/crm/plans/${id}/delete`);
const cloneCrmPlan = (id) => httpClient.post(`/api/crm/plans/${id}/replicate`);

const changePlanStatus3d = ({ id, status }) => httpClient.post(`/api/crm/plans/${id}/status_3d/${status}`);

const getCrmPlan3dPreview = (id, presetId, force = false, choice = {}) => httpClient
  .post(`/api/crm/plans/${id}/get_3d_preview`, { plan_preset_id: presetId, force, ...choice });
const getCrmPlan3dResult = (id, presetId, force = false, choice = {}) => httpClient
  .post(`/api/crm/plans/${id}/get_3d_result`, { plan_preset_id: presetId, force, ...choice });
const getCrmPlan3dResultLow = (id, force = false, choice = {}) => httpClient
  .post(`/api/crm/plans/${id}/get_3d_low`, { force, ...choice });
const getCrmPlan3dResultHigh = (id, force = false, choice = {}) => httpClient
  .post(`/api/crm/plans/${id}/get_3d_high`, { force, ...choice });
const getCrmPlan3dResultUltraHigh = (id, force = false, choice = {}) => httpClient
  .post(`/api/crm/plans/${id}/get_3d_ultra_high`, { force, ...choice });
const getCrmPlan3dResultUnlit = (id, force = false, choice = {}) => httpClient
  .post(`/api/crm/plans/${id}/get_3d_unlit`, { force, ...choice });

const runUnrealGeneration = (id, settings, force = false) => httpClient
  .post(`/api/crm/plans/${id}/get_3d`, { ...settings, force });

const runGltfRender = (id, formData, force = false, choice = {}) => {
  Object.entries(choice).forEach(([key, value]) => formData.set(key, value));
  formData.set('force', force ? '1' : '0');

  return httpClient.post(
    `/api/crm/plans/${id}/render_gltf`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

const changeCameraNavigationMode = (id, cameraNavigation) => httpClient
  .post(`/api/plans/${id}/change_capability`, { cameraNavigation });

const getCrmPlanFurniturePlacement = (id, presetId, force = false) => httpClient
  .post(`/api/crm/plans/${id}/run_furniture_placement`, { plan_preset_id: presetId, force });
const changePlanStrategicData = (id, region, developer, complex, address) => {
  httpClient.post(`api/crm/plans/${id}/change_strategic_data`, {
    region, developer, complex, address,
  });
};

const getDesignFiles = () => httpClient.get('/api/furniture/design_files');
const createDesignFile = (fd) => httpClient.post('/api/furniture/design_files', fd);
const deleteDesignFile = (id) => httpClient.delete(`/api/furniture/design_files/${id}`);
const changeDesignFile = (id, fd) => httpClient.post(`/api/furniture/design_files/${id}`, fd);
const downloadDesignFile = (id) => httpClient.get(`api/furniture/design_files/${id}`);

const getFurnitureList = (params) => httpClient.get(
  '/api/furniture',
  { params, paramsSerializer: (s) => qs.stringify(s) },
);
const getFurniture = (id) => httpClient.get(`api/furniture/${id}`);
const getFurnitureSimpleList = (params = {}) => httpClient.get('/api/furniture/simple', { params });
const getDeletedFurnitureList = () => httpClient.get('/api/furniture/deleted');
const updateFurnitureParams = ({ id, params }) => httpClient.patch(`/api/furniture/${id}`, params);
const deleteFurniture = (id) => httpClient.delete(`/api/furniture/${id}`);
const invalidateFurniture = () => httpClient.delete('/api/furniture/invalidate');
const restoreFurniture = (id) => httpClient.patch(`/api/furniture/${id}/restore`);
const getFurnitureTags = () => httpClient.get('/api/furniture/tags');
const createFurnitureTag = (payload) => httpClient.post('/api/furniture/tags', payload);
const updateFurnitureTag = (id, payload) => httpClient.put(`/api/furniture/tags/${id}`, payload);
const deleteFurnitureTag = (id) => httpClient.delete(`/api/furniture/tags/${id}`);
const getFurnitureGroups = (params = {}) => httpClient.get('/api/furniture/groups', {
  params,
  paramsSerializer: (s) => qs.stringify(s, { arrayFormat: 'brackets' }),
});
const updateFurnitureGroup = (id, formData) => httpClient.post(`/api/furniture/groups/${id}`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
const deleteFurnitureGroup = (id) => httpClient.delete(`/api/furniture/groups/${id}`);
const getFurnitureGroupTags = () => httpClient.get('/api/furniture/group_tags');
const createFurnitureGroupTag = (payload) => httpClient.post('/api/furniture/group_tags', payload);
const updateFurnitureGroupTag = (id, payload) => httpClient.put(`/api/furniture/group_tags/${id}`, payload);
const deleteFurnitureGroupTag = (id) => httpClient.delete(`/api/furniture/group_tags/${id}`);
const getTagCategories = (params = {}) => httpClient.get('/api/tag-categories', {
  params,
  paramsSerializer: (s) => qs.stringify(s),
});
const createTagCategory = (payload) => httpClient.post('/api/tag-categories', payload);
const updateTagCategory = (id, payload) => httpClient.put(`/api/tag-categories/${id}`, payload);
const deleteTagCategory = (id) => httpClient.delete(`/api/tag-categories/${id}`);
const createFurniture = (request) => {
  const fd = new FormData();

  const entries = Object.entries(request);

  entries.forEach(([key, value]) => {
    if (key === 'textures') {
      value.forEach((texture, index) => {
        fd.append(`textures[${index}][type]`, texture.type);
        fd.append(`textures[${index}][material_id]`, texture.material_id);
        fd.append(`textures[${index}][file]`, texture.file);
      });
    } else {
      fd.append(key, value);
    }
  });

  return httpClient.post('api/furniture/upload', fd);
};

const getPlanBatchFormData = () => httpClient.get('/api/plans/batch-actions/form-data');

const executePlanBatchAction = (formData) => httpClient.post(
  '/api/plans/batch-actions/process',
  formData,
  {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 30000,
  },
);

const checkPlanBatchStatus = (logId) => httpClient.get(`/api/plans/batch-actions/${logId}/status`);

const loadPlanBatchHistory = (params) => httpClient.get('/api/plans/batch-actions/history', {
  params,
  paramsSerializer: (s) => qs.stringify(s),
});

const getPlanBatchLogDetails = (logId) => httpClient.get(`/api/plans/batch-actions/${logId}/details`);

const getPlanBatchSendPreview = (planInputs) => httpClient.post(
  '/api/plans/batch-actions/send-preview',
  { plan_inputs: planInputs },
);

const deleteFurnitureTopView = (id) => httpClient.delete(`api/furniture/${id}/top_view`, { furniture_id: id });

const deleteFurniturePreview = (id) => httpClient.delete(`api/furniture/${id}/preview`, { furniture_id: id });

const getMaterialsList = (params) => httpClient.get('/api/unreal_materials', { params });
const deleteMaterial = ({ id }) => httpClient.patch(`/api/unreal_materials/${id}`, { is_archived: true });
const invalidateMaterials = () => httpClient.delete('/api/unreal_materials/invalidate');
const restoreMaterial = (id) => httpClient.patch(`/api/unreal_materials/${id}`, { is_archived: false });
const getMaterialById = (id) => httpClient.get(`api/unreal_materials/${id}`);
const updateMaterialPlanStyles = (id, planStyles) => httpClient.put(`/api/unreal_materials/${id}/plan_styles`, {
  plan_style_ids: planStyles,
});
const updateMaterialRoomTypes = (id, roomTypes) => httpClient.put(`/api/unreal_materials/${id}/room_types`, {
  room_type_ids: roomTypes,
});
const getMaterialsTags = (params) => httpClient.get('/api/unreal_materials/tags', { params });
const updateMaterialTags = (id, tags) => httpClient.put(`/api/unreal_materials/${id}/tags`, {
  tag_ids: tags,
});
const createMaterialTag = (request) => httpClient.post('/api/unreal_materials/tags', request);

const getAperturesList = (params) => httpClient.get('/api/apertures', { params });
const getApertureById = (id) => httpClient.get(`api/apertures/${id}`);
const archiveAperture = (id) => httpClient.delete(`/api/apertures/${id}`);
const restoreAperture = (id) => httpClient.patch(`/api/apertures/${id}/restore`);
const updateAperture = (id, payload) => httpClient.patch(`/api/apertures/${id}`, payload);
const getApertureTags = () => httpClient.get('/api/apertures/tags');
const createApertureTag = (payload) => httpClient.post('/api/apertures/tags', payload);
const updateApertureTag = (id, payload) => httpClient.put(`/api/apertures/tags/${id}`, payload);
const deleteApertureTag = (id) => httpClient.delete(`/api/apertures/tags/${id}`);

const getMoldingsList = (params) => httpClient.get('/api/moldings', { params });
const getMoldingById = (id) => httpClient.get(`/api/moldings/${id}`);
const archiveMolding = (id) => httpClient.delete(`/api/moldings/${id}`);
const restoreMolding = (id) => httpClient.patch(`/api/moldings/${id}/restore`);
const updateMolding = (id, payload) => httpClient.patch(`/api/moldings/${id}`, payload);
const getMoldingTags = () => httpClient.get('/api/moldings/tags');
const createMoldingTag = (payload) => httpClient.post('/api/moldings/tags', payload);
const updateMoldingTag = (id, payload) => httpClient.put(`/api/moldings/tags/${id}`, payload);
const deleteMoldingTag = (id) => httpClient.delete(`/api/moldings/tags/${id}`);
const getStyleMoldings = (id) => httpClient.get(`/api/plans/styles/${id}/moldings`);
const updateStyleMoldings = (id, moldings) => httpClient.put(`/api/plans/styles/${id}/moldings`, { moldings });

const getSvgList = (params = {}) => httpClient.get('/api/svg_editor/icons', { params });

const createSvg = (data) => httpClient.post('/api/svg_editor/icons', data);
const invalidateSvg = () => httpClient.delete('/api/svg_editor/icons/invalidate');
const deleteSvgByName = (directory, name) => httpClient.delete(`/api/svg_editor/icons/${directory}/${name}`);
const archiveSvgByName = (name) => httpClient.post(`/api/svg_editor/icons/${name}/archive`);
const restoreSvgByName = (name) => httpClient.patch(`/api/svg_editor/icons/${name}`);

const getSvgTags = () => httpClient.get('/api/svg_editor/icons/tags');
const createSvgTag = (tagData) => httpClient.post('/api/svg_editor/icons/tags', tagData);
const updateSvgTag = (id, tagData) => httpClient.put(`/api/svg_editor/icons/tags/${id}`, tagData);
const deleteSvgTag = (id) => httpClient.delete(`/api/svg_editor/icons/tags/${id}`);

const getSvgTypes = () => httpClient.get('/api/svg_editor/icons/types');
const createSvgType = (typeData) => httpClient.post('/api/svg_editor/icons/types', typeData);
const updateSvgType = (id, typeData) => httpClient.put(`/api/svg_editor/icons/types/${id}`, typeData);
const deleteSvgType = (id) => httpClient.delete(`/api/svg_editor/icons/types/${id}`);

const getSvgFileTags = (fileName) => httpClient.get(`/api/svg_editor/icons/${fileName}/tags`);
const updateSvgFileTags = (fileName, tagIds) => {
  const nevermind = !!fileName;
  return httpClient.put(`/api/svg_editor/icons/${fileName}/tags`, { tag_ids: tagIds });
};

const updateSvgIconType = (fileName, typeName) => {
  const nevermind = !!fileName;
  return httpClient.put(`/api/svg_editor/icons/files/${fileName}/type`, { type_name: typeName });
};

const getApartmentList = (params) => httpClient.get(
  '/api/crm/apartments',
  { params, paramsSerializer: (s) => qs.stringify(s) },
);
const getApartmentResponsiblesMatrix = (params) => httpClient.get(
  '/api/crm/apartments/responsibles/matrix',
  { params, paramsSerializer: (s) => qs.stringify(s) },
);
const updateApartmentResponsiblesBatch = (items) => httpClient
  .post('/api/crm/apartments/responsibles/batch', items);
const updateApartmentResponsibles = (id, responsibles) => httpClient
  .put(`/api/crm/apartments/${id}/responsibles`, responsibles);
const updateApartment = (id, {
  name, description, additional, region, developer, complex, address,
  sameNinjaFor2dAnd3dPlans, sameReviewerFor2dAnd3dPlans, integrationWithBitrix, projectType,
  widgetBrandingId, defaultPresetId, defaultUnrealEnvironmentId,
}) => httpClient
  .patch(`/api/crm/apartments/${id}`, {
    name,
    description,
    additional,
    region,
    developer,
    complex,
    address,
    same_ninja_for_2d_and_3d_plans: sameNinjaFor2dAnd3dPlans,
    same_reviewer_for_2d_and_3d_plans: sameReviewerFor2dAnd3dPlans,
    integration_with_bitrix: integrationWithBitrix,
    project_type: projectType,
    widget_branding_id: widgetBrandingId,
    default_preset_id: defaultPresetId,
    default_unreal_environment_id: defaultUnrealEnvironmentId,
  });

const changeApartmentPriority = (id, { priority }) => httpClient
  .post(`/api/crm/apartments/${id}/change_priority`, { priority });

const getApartmentsPlansList = (id, type) => httpClient.get(`/api/crm/apartments/${id}/plans/${type}`);

const getNinjaStatisticPlans = (id) => httpClient.get(`api/crm/statistic/ninja/${id}/plans`);
const getCrmPlanSendStatus = (id) => httpClient.get(`/api/crm/plans/${id}/send_status`);

const sendCrmPlanToCustomer = (id) => httpClient.post(`/api/crm/plans/${id}/send_to_customer`);

const getSameCrmPlans = (id, type = 0) => httpClient.get(`/api/crm/plans/${id}/same?type=${type}`);
const getOriginalPlansArchive = (id) => httpClient
  .get(`/api/crm/apartments/${id}/download_orginal_plans`, fileDownloadConfig);
const enableSelectedPlans3d = ({ ids }) => {
  httpClient
    .post('api/crm/plans/batch/enable_3d', { ids });
};
const setApartmentPlansPreset = ({ newPresetId, ids }) => httpClient.post(
  'api/crm/plans/batch/set_preset',
  { preset_id: newPresetId, ids },
);

const mergeCrmPlan = (id, { type, crmPlanId }) => httpClient
  .post(`/api/crm/plans/${id}/merge`, { type, crm_plan_id: crmPlanId });

const addBitrixLeadToCrmPlan = (id, { leadId }) => httpClient
  .post(`/api/crm/plans/${id}/bitrix/leads`, { lead_id: leadId });

const removeBitrixLeadToCrmPlan = (id, leadId) => httpClient
  .delete(`/api/crm/plans/${id}/bitrix/leads/${leadId}`);

const getNinja2dStatisticPlans = (id, { afterDate, beforeDate }) => httpClient
  .get(`api/crm/statistic/ninja/${id}/plans`, {
    params: {
      filter: {
        after_date: afterDate,
        before_date: beforeDate,
      },
    },
    paramsSerializer: (s) => qs.stringify(s),
  });
const getNinja3dStatisticPlans = (id, { afterDate, beforeDate }) => httpClient
  .get(`api/crm/statistic/ninja_3d/${id}/plans`, {
    params: {
      filter: {
        after_date: afterDate,
        before_date: beforeDate,
      },
    },
    paramsSerializer: (s) => qs.stringify(s),
  });
const getPaticularNinja2dStatisticFile = (id, { afterDate, beforeDate }) => httpClient
  .get(
    `api/crm/statistic/ninja/${id}/excel`,

    {

      ...fileDownloadConfig,
      params: {
        filter: {
          after_date: afterDate,
          before_date: beforeDate,
        },
      },
      paramsSerializer: (s) => qs.stringify(s),
    },
  );

const getPaticularNinja3dStatisticFile = (id, { afterDate, beforeDate }) => httpClient
  .get(
    `api/crm/statistic/ninja_3d/${id}/excel`,

    {
      ...fileDownloadConfig,
      params: {
        filter: {
          after_date: afterDate,
          before_date: beforeDate,
        },
      },
      paramsSerializer: (s) => qs.stringify(s),
    },

  );

const getStylesListForFurniture = () => httpClient.get('api/plans/styles');
const createStyleForFurniture = (request) => httpClient.post('api/plans/styles', request);
const editStyleForFurniture = ({ id, name }) => httpClient.patch(`api/plans/styles/${id}`, { name });
const deleteStyleForFurniture = (id) => httpClient.delete(`/api/plans/styles/${id}`);

const getRoomTypesList = (params = {}) => httpClient.get('api/furniture/room_types', { params });
const createRoomType = (request) => httpClient.post('api/furniture/room_types', request);
const editRoomType = ({ id, name, is_service: isService }) => httpClient.patch(`api/furniture/room_types/${id}`, {
  name,
  is_service: isService,
});
const archiveRoomType = (id) => httpClient.delete(`/api/furniture/room_types/${id}`);
const restoreRoomType = (id) => httpClient.patch(`/api/furniture/room_types/${id}/restore`);
const createPreset = (request) => httpClient.post('api/crm/preset', request);
const getPresets = (params = {}) => httpClient.get('api/crm/preset', { params });
const editPreset = ({
  id, name, planStyleId, furnitureDesignFileId,
}) => httpClient.patch(`api/crm/preset/${id}`, {
  name,
  plan_style_id: planStyleId,
  furniture_design_file_id: furnitureDesignFileId,
});
const changeCrmPlanPreset = ({
  id, presetId,
}) => httpClient.post(`api/crm/plans/${id}/change_preset/${presetId}`, {
  preset_id: presetId,
});
const deletePreset = (id) => httpClient.delete(`/api/crm/preset/${id}`);

const getUnrealEnvironment = () => httpClient.get('/api/plans/unreal_environment');
const createUnrealEnvironment = (formData) => httpClient.post('/api/plans/unreal_environment', formData);
const editUnrealEnvironment = ({ id, formData }) => {
  formData.append('_method', 'PATCH');
  return httpClient.post(
    `/api/plans/unreal_environment/${id}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
    .then((response) => response.data);
};

const deleteUnrealEnvironment = (id) => httpClient.delete(`/api/plans/unreal_environment/${id}`);
const restoreUnrealEnvironment = (id) => httpClient.post(`/api/plans/unreal_environment/${id}/restore`);
const invalidateUnrealEnvironment = () => httpClient.delete('/api/plans/unreal_environment/invalidate');

const changeUnrealEnvironment = ({
  id, unrealEnvironmentId,
}) => httpClient.post(`api/crm/plans/${id}/set_unreal_environment/${unrealEnvironmentId}`, {
  unreal_environment_id: unrealEnvironmentId,
});

const changeManager = (id, managerId, is3d) => httpClient.post(`/api/crm/plans/${id}/change_manager`, {
  manager_id: managerId,
  is_3d: is3d,
});

const changeCutter = (id, cutterId, is3d) => httpClient.post(`/api/crm/plans/${id}/change_cutter`, {
  cutter_id: cutterId,
  is_3d: is3d,
});

const changeReviewer = (id, reviewerId, is3d) => httpClient.post(`/api/crm/plans/${id}/change_reviewer`, {
  reviewer_id: reviewerId,
  is_3d: is3d,
});

const getBrandingsList = (params) => httpClient.get('api/plans/widget_brandings', { params });
const getMinimalBrandingsList = () => httpClient.get('api/plans/widget_brandings/minimal');
// Публичные данные брендинга по id — нужен домен для открытия клиентского виджета.
const getWidgetBrandingById = (id) => httpClient.get(`api/plans/widget_branding/${id}`);
const getBranding = (id) => httpClient.get(`api/plans/widget_brandings/${id}`);
const createBranding = (request) => httpClient.post('api/plans/widget_brandings', request);
const editBranding = (request) => httpClient.post(`api/plans/widget_brandings/${request.id}`, request);
const deleteBranding = (id) => httpClient.delete(`api/plans/widget_brandings/${id}`, { widgetBranding: id });
const getPlanBranding = (planId) => httpClient.get(`api/crm/plans/${planId}/branding`);
const getAvailablePlanBrandings = () => httpClient.get('api/crm/plans/brandings/available');
const attachPlanBranding = (planId, widgetBrandingId) => httpClient
  .post(`api/crm/plans/${planId}/branding/attach`, { widget_branding_id: widgetBrandingId });
const detachPlanBranding = (planId, confirm = true) => httpClient
  .post(`api/crm/plans/${planId}/branding/detach`, { confirm });
const createPlanBranding = (planId, formData) => httpClient
  .post(`api/crm/plans/${planId}/branding/create`, formData);
const getServicePlanBranding = (planId) => httpClient.get(`api/plans/${planId}/branding`);
const getAvailableServicePlanBrandings = () => httpClient.get('api/plans/brandings/available');
const attachServicePlanBranding = (planId, widgetBrandingId) => httpClient
  .post(`api/plans/${planId}/branding/attach`, { widget_branding_id: widgetBrandingId });
const detachServicePlanBranding = (planId, confirm = true) => httpClient
  .post(`api/plans/${planId}/branding/detach`, { confirm });
const createServicePlanBranding = (planId, formData) => httpClient
  .post(`api/plans/${planId}/branding/create`, formData);
const getPlansForBranding = (brandingId) => httpClient.get(`api/crm/plans/branding/${brandingId}/plans`);

const getChecklists = () => httpClient.get('api/checklists');
const getCrmChecklists = (id) => httpClient.get(`/api/crm/plans/${id}/checklists`);
const getServiceChecklists = (id) => httpClient.get(`/api/plans/${id}/checklists`);
const createChecklist = (request) => httpClient.post('api/checklists', request);
const editChecklist = (request) => httpClient.put(`api/checklists/${request.id}`, request);
const deleteChecklist = (id) => httpClient.delete(`api/checklists/${id}`);

const getPlanGuards = (params) => httpClient.get('api/plan_guards', { params });
const getPlanGuardById = (id) => httpClient.get(`api/plan_guards/${id}`);
const createPlanGuard = (request) => httpClient.post('api/plan_guards', request);
const editPlanGuard = (id, request) => httpClient.put(`api/plan_guards/${id}`, request);
const deletePlanGuard = (id) => httpClient.delete(`api/plan_guards/${id}`);

const syncCustomerPlanGuards = (id, request) => httpClient.put(`api/plan_guards/bindings/customer/${id}`, request);
const syncCrmApartmentPlanGuards = (id, request) => httpClient
  .put(`api/plan_guards/bindings/crm_apartment/${id}`, request);
const syncCrmPlanPlanGuards = (id, request) => httpClient.put(`api/plan_guards/bindings/crm_plan/${id}`, request);

const createLanding = (request) => httpClient.post('api/landings', request);
const editLanding = (request) => httpClient.post(`api/landings/${request.id}`, request);

const getDesignFile = (id) => httpClient.get(`api/furniture/design_files/${id}`);
const createNewDesignFile = (id, newName, postData) => httpClient.post(`api/furniture/design_files/${id}/change`, {
  new_furniture_design_file_name: newName,
  furniture: postData,
});

const getCuttingPlansList = (params) => httpClient.get('/api/crm/plans/cropping', {
  params,
  paramsSerializer: (s) => qs.stringify(s),
});
const startPlanCutting = (planId) => httpClient.post(`/api/crm/plans/cropping/${planId}/start`, { id: planId });
const getToWorkPlanCutting = () => httpClient.post('/api/crm/plans/cropping/get_to_work');
const showCuttingPlan = (planId) => httpClient.get(`/api/crm/plans/cropping/${planId}`, { id: planId });

const postponeCuttingPlan = (planId) => httpClient.post(`/api/crm/plans/cropping/${planId}/postpone`, { id: planId });
const archiveCuttingPlan = (id) => httpClient.post(`/api/crm/plans/cropping/${id}/archive`);
const unarchiveCuttingPlan = (id) => httpClient.post(`/api/crm/plans/cropping/${id}/unarchive`);
const unpostponeCuttingPlan = (id) => httpClient.post(`/api/crm/plans/cropping/${id}/unpostpone`);

const returnCuttingPlan = (id) => httpClient.post(`/api/crm/plans/cropping/${id}/return`);

const declineCuttingPlan = (planId, declineReason = null) => httpClient
  .post(`/api/crm/plans/${planId}/decline`, { id: planId, decline_reason: declineReason });

const uploadImagesCuttingPlan = (planId, formData) => httpClient
  .post(`/api/crm/plans/cropping/${planId}/upload_images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    id: planId,
  });

const sendCuttingPlanForProcessing = (planId, fd) => httpClient
  .post(`/api/crm/plans/cropping/${planId}/send_for_processing`, fd, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    id: planId,
  });

const submitCuttingPlanForProcessing = (planId, fd) => httpClient
  .post(`api/crm/plans/cropping/${planId}/submit_for_processing`, fd, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    id: planId,
  });

const reloadFromStep = (id, { step }) => httpClient
  .post(`/api/plans/${id}/reload_step`, { step });

const resetFromStep = (id, { step }) => httpClient
  .post(`/api/plans/${id}/reset_to_error`, { step });

const getPlans = (type, params) => {
  switch (type) {
    case PlanType.Type2d:
      return httpClient.get('/api/crm/plans', { params });
    case PlanType.Type3d:
      return httpClient.get('/api/crm/plans/list_3d', { params });
    default:
      throw Error('Undefined type');
  }
};

const getServicePlans = async (type, {
  status, userIds, tagIds, name, region, developer, complex, address, page, dates, crmTagName, externalId,
  perPage, withSvg,
}) => httpClient
  .get(`/api/crm/plans${type === PlanType.Type3d ? '/list_3d' : ''}`, {
    params: {
      filter: {
        status,
        user_ids: userIds,
        tag_ids: tagIds,
        name: name?.split('|'),
        region,
        developer,
        complex,
        address,
        after_date: dates?.[0]?.toISOString() ?? null,
        before_date: dates?.[1]?.toISOString() ?? null,
        crm_tag_name: crmTagName,
        external_id: externalId,
      },
      page,
      per_page: perPage,
      with_svg: withSvg,
    },
    paramsSerializer: (s) => qs.stringify(s),

  });

const getPlanMaskToWork = (dataset) => httpClient
  .get(`/api/neural_dataset_selection_service/datasets/${dataset}/get_plan_to_work`);
const datasetSelectionPlans = () => httpClient
  .get('/api/neural_dataset_selection_service/');
const datasetSelectionDatasets = () => httpClient
  .get('/api/neural_dataset_selection_service/datasets');
const datasetSelectionPlansReject = (id, dataset) => httpClient
  .post(`/api/neural_dataset_selection_service/datasets/${dataset}/dataset_plans/${id}/reject`);
const datasetSelectionPlansAccept = (id, dataset) => httpClient
  .post(`/api/neural_dataset_selection_service/datasets/${dataset}/dataset_plans/${id}/accept`);
const datasetSelectionPlansReport = (id, dataset) => httpClient
  .post(`/api/neural_dataset_selection_service/datasets/${dataset}/dataset_plans/${id}/report`);

const getStrategicData = (type) => httpClient.get(`/api/crm/${type}/strategic_data`);

const getStyleMaterials = () => httpClient.get('/api/furniture/style_materials');
const createStyleMaterial = ({ name, textures }) => {
  const fd = new FormData();

  fd.append('name', name);

  textures.forEach(({ type, texture }, index) => {
    fd.append(`textures[${index}][type]`, type);
    fd.append(`textures[${index}][texture]`, texture);
  });

  return httpClient.post('/api/furniture/style_materials', fd);
};
const deleteStyleMaterial = (id) => httpClient.delete(`/api/furniture/style_materials/${id}`);

const createRoomStyleItem = ({
  type, styleId, roomTypeId, styleMaterialIds,
}) => httpClient.post('/api/plans/room_style_items', {
  type,
  style_id: styleId,
  room_type_id: roomTypeId,
  style_material_ids: styleMaterialIds,
});

const deleteRoomStyleItem = (id) => httpClient.delete(`/api/plans/room_style_items/${id}`);

const getWidgetVariant = (widgetVariantId) => httpClient
  .get(`api/crm/plans/widget/variant/${widgetVariantId}`, { widgetVariant_id: widgetVariantId });

const createWidgetVariant = (addVariantsForm, crmPlanId) => httpClient
  .post('api/crm/plans/widget/variant', { variants: addVariantsForm.variants, crm_plan_id: crmPlanId });

const updateWidgetVariant = (requestData, widgetVariantId) => httpClient
  .patch(`api/crm/plans/widget/variant/${widgetVariantId}`, requestData);

const deleteWidgetVariant = (widgetVariantId) => httpClient
  .delete(`api/crm/plans/widget/variant/${widgetVariantId}`, { widgetVariant_id: widgetVariantId });

const updateVariantsPriority = (requestData) => httpClient
  .patch('api/crm/plans/widget/variants/priorities', requestData);

const getCustomersList = () => httpClient.get('api/users/customers');
const createCustomer = (request) => httpClient.post('api/users/customers', request);
const editCustomer = (request) => httpClient.post(`api/users/customers/${request.id}`, request);
const deleteCustomer = (id) => httpClient.delete(`api/users/customers/${id}`);

const getWidgetLinkForCrmId = (id) => httpClient.get(`api/crm/plans/${id}/widget`);
const getWidgetLinkForPlanId = (id) => httpClient.get(`api/plans/${id}/v2/widget`);
const invalidateWidgetCache = (id) => httpClient.post(`/api/plans/${id}/widgets/cache`);
const invalidateCrmWidgetCache = (id) => httpClient.post(`/api/crm/plans/${id}/widgets/cache`);

const findSimilarPlansAi = async (formData) => {
  const response = await httpClient.post('/api/v1/bovw/floorplan/find_similar/', formData, {
    baseURL: config.apiBovwUrl,
  });
  return response.data;
};

const getAdjustmentsByCrmPlanId = (id) => httpClient.get('/api/crm/adjustments/', {
  params: { crm_plan_id: id },
});

const createLandingTariff = (landingId, formData) => httpClient.post(`api/landings/${landingId}/tariffs`, formData);
const updateLandingTariff = (landingId, tariffId, formData) => httpClient.put(
  `api/landings/${landingId}/tariffs/${tariffId}`,
  formData,
  {
    headers: {
      'Content-Type': 'application/json',
    },
  },
);
const deleteLandingTariff = (tariffId) => httpClient.delete(`api/landings/tariffs/${tariffId}`);

const getWebhooks = () => httpClient.get('/api/webhooks');
const getWebhookStrategies = () => httpClient.get('/api/webhooks/strategies');
const getWebhook = (id) => httpClient.get(`/api/webhooks/${id}`);
const createWebhook = (request) => httpClient.post('/api/webhooks', request);
const updateWebhook = (id, request) => httpClient.put(`/api/webhooks/${id}`, request);
const deleteWebhook = (id) => httpClient.delete(`/api/webhooks/${id}`);

const createMattermostLink = () => httpClient.post('/api/mattermost/link');
const callbackMattermostLink = (code, state) => httpClient.get('/api/mattermost/callback', {
  params: { code, state },
});

const getUserTelegramId = (id) => httpClient.get(`/api/users/${id}/telegram-id`);

const setUserTelegramId = (id, telegramId) => httpClient.patch(`/api/users/${id}/telegram-id`, {
  telegram_id: telegramId,
});

const removeUserTelegramId = (id) => httpClient.delete(`/api/users/${id}/telegram-id`);

const getUserSettings = (id) => httpClient.get(`/api/users/${id}/settings`);

const updateUserSettings = (id, settings) => httpClient.patch(`/api/users/${id}/settings`, {
  settings,
});

const updateUserTelegramNotificationStatus = (id, data) => httpClient.patch(
  `/api/users/${id}/telegram-notification-status`,
  data,
);

const getCrmPlanActivityLogs = async (id, { planType, actionCategory }) => httpClient
  .get(`/api/crm/plans/${id}/logs`, {
    params: {
      plan_type: planType,
      action_category: actionCategory,
    },
    paramsSerializer: (s) => qs.stringify(s),
  });

const getServicePlanActivityLogs = async (id, { actionCategory }) => httpClient
  .get(`/api/plans/${id}/logs`, {
    params: {
      action_category: actionCategory,
    },
    paramsSerializer: (s) => qs.stringify(s),
  });

const getCrmPlanRevisions = async (id, { kind, type }) => httpClient
  .get(`/api/crm/plans/${id}/revisions`, { params: { kind, type } });

const getServicePlanRevisions = async (id, { kind, type }) => httpClient
  .get(`/api/plans/${id}/revisions`, { params: { kind, type } });

const getCrmPlanRevision = async (id, revisionId) => httpClient
  .get(`/api/crm/plans/${id}/revisions/${revisionId}`);

const getServicePlanRevision = async (id, revisionId) => httpClient
  .get(`/api/plans/${id}/revisions/${revisionId}`);

const updateCrmPlanRevision = async (id, revisionId, { label }) => httpClient
  .patch(`/api/crm/plans/${id}/revisions/${revisionId}`, { label });

const updateServicePlanRevision = async (id, revisionId, { label }) => httpClient
  .patch(`/api/plans/${id}/revisions/${revisionId}`, { label });

const typePayload = (type) => (type === undefined || type === null ? {} : { type });
const typeParams = (type, extra = {}) => ({ ...typePayload(type), ...extra });

const takeToReview = ({ type } = {}) => httpClient.post('/api/crm/reviews/take', typePayload(type));
const getReviewerBoard = ({ type } = {}) => httpClient
  .get('/api/crm/reviews/me', { params: typeParams(type) });
const getReviewsAvailableCount = ({ type } = {}) => httpClient
  .get('/api/crm/reviews/available_count', { params: typeParams(type) });
const getPlanReviews = ({ planId, type }) => httpClient
  .get(`/api/crm/plans/${planId}/reviews`, { params: typeParams(type) });
const openReview = ({ planId, type }) => httpClient
  .post(`/api/crm/plans/${planId}/reviews`, { type });
const markReviewStage = ({ planId, type, stage }) => httpClient
  .post(`/api/crm/plans/${planId}/reviews/stage`, { ...typePayload(type), stage });
const submitReview = ({ planId, type, verdict }) => httpClient
  .post(`/api/crm/plans/${planId}/reviews/submit`, { ...typePayload(type), verdict });
const getPlanIssues = ({
  planId, type, planVariantId, reviewId, excludeReviewId,
}) => httpClient
  .get(`/api/crm/plans/${planId}/issues`, {
    params: {
      type,
      plan_id: planVariantId,
      review_id: reviewId,
      exclude_review_id: excludeReviewId,
    },
  });
const createIssue = ({ planId, formData }) => httpClient
  .post(`/api/crm/plans/${planId}/issues`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
const deleteIssue = ({ issueId }) => httpClient.delete(`/api/crm/issues/${issueId}`);
const markIssueFixed = ({ issueId, formData }) => httpClient
  .post(`/api/crm/issues/${issueId}/fixed`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
const markIssueCouldntFix = ({ issueId, comment }) => httpClient
  .post(`/api/crm/issues/${issueId}/couldnt_fix`, { comment });
const confirmIssue = ({ issueId }) => httpClient.post(`/api/crm/issues/${issueId}/confirm`);
const reopenIssue = ({ issueId, comment }) => httpClient
  .post(`/api/crm/issues/${issueId}/reopen`, { comment });
const getNinjaBoard = ({ type, tab }) => httpClient
  .get('/api/crm/ninjas/me/board', { params: typeParams(type, { tab }) });
const openFix = ({ planId, type }) => httpClient.post(`/api/crm/plans/${planId}/fix/open`, { type });
const submitFix = ({ planId, type }) => httpClient.post(`/api/crm/plans/${planId}/fix/submit`, { type });
const acknowledgeRecommendations = ({ planId, type }) => httpClient
  .post(`/api/crm/plans/${planId}/recommendations/acknowledge`, { type });
const uploadCommentImage = ({ planId, file }) => {
  const formData = new FormData();
  formData.append('image', file);

  return httpClient.post(`/api/crm/plans/${planId}/comments/upload_image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
const getIssueCategories = () => httpClient.get('/api/crm/issue_categories');
const createIssueCategory = (request) => httpClient.post('/api/crm/issue_categories', request);
const updateIssueCategory = ({ id, ...data }) => httpClient.patch(`/api/crm/issue_categories/${id}`, data);
const deleteIssueCategory = (id) => httpClient.delete(`/api/crm/issue_categories/${id}`);
const updateApartmentDeadline = (id, payload) => httpClient.patch(`/api/crm/apartments/${id}/deadline`, payload);

const updateApartmentBrp = (id, payload) => httpClient.patch(`/api/crm/apartments/${id}/brp`, payload);

const getPayrollPeriods = () => httpClient.get('/api/crm/payroll/periods');
const getMyPayroll = ({ period } = {}) => httpClient.get('/api/crm/payroll/me', { params: { period } });
const getPayrollEmployees = () => httpClient.get('/api/crm/payroll/employees');
const getPayrollSummary = ({ period, workType } = {}) => httpClient
  .get('/api/crm/payroll/summary', { params: { period, work_type: workType } });
const getEmployeePayroll = (id, { period } = {}) => httpClient
  .get(`/api/crm/payroll/employees/${id}`, { params: { period } });
const setPayrollAmount = (planId, {
  type, workType, amount, comment,
}) => httpClient
  .put(`/api/crm/payroll/plans/${planId}/amount`, {
    type, work_type: workType, amount, comment,
  });
const resetPayrollAmount = (planId, { type, workType }) => httpClient
  .delete(`/api/crm/payroll/plans/${planId}/amount`, { data: { type, work_type: workType } });
const getPayrollAmountLog = (planId) => httpClient.get(`/api/crm/payroll/plans/${planId}/amount/log`);
const getWorkSchedulePeriods = () => httpClient.get('/api/crm/work_schedules/periods');
const getMyWorkScheduleStatus = () => httpClient.get('/api/crm/work_schedules/me/status');
const getMyWorkSchedule = ({ period } = {}) => httpClient
  .get('/api/crm/work_schedules/me', { params: { period } });
const saveMyWorkSchedule = (payload) => httpClient
  .put('/api/crm/work_schedules/me', payload);
const getWorkScheduleEmployees = ({ period } = {}) => httpClient
  .get('/api/crm/work_schedules/employees', { params: { period } });
const getEmployeeWorkSchedule = (id, { period } = {}) => httpClient
  .get(`/api/crm/work_schedules/employees/${id}`, { params: { period } });
const saveEmployeeWorkSchedule = (id, payload) => httpClient
  .put(`/api/crm/work_schedules/employees/${id}`, payload);
const getEmployeeWorkScheduleLog = (id, { period } = {}) => httpClient
  .get(`/api/crm/work_schedules/employees/${id}/log`, { params: { period } });
const getEmployeeWorkScheduleBrief = (id, { period } = {}) => httpClient
  .get(`/api/crm/work_schedules/employees/${id}/brief`, { params: { period } });
const updateCrmPlanDeadline = (id, payload) => httpClient.patch(`/api/crm/plans/${id}/deadline`, payload);

export const api = {
  takeToReview,
  getReviewerBoard,
  getReviewsAvailableCount,
  getPlanReviews,
  openReview,
  markReviewStage,
  submitReview,
  getPlanIssues,
  createIssue,
  deleteIssue,
  markIssueFixed,
  markIssueCouldntFix,
  confirmIssue,
  reopenIssue,
  getNinjaBoard,
  openFix,
  submitFix,
  acknowledgeRecommendations,
  uploadCommentImage,
  getIssueCategories,
  createIssueCategory,
  updateIssueCategory,
  deleteIssueCategory,
  updateApartmentDeadline,
  updateCrmPlanDeadline,

  getCrmPlanById,
  overridePlan,
  getCrmPlan3dById,
  getCrmPlanState,

  getServicePlanList,
  getServicePlanById,
  getServicePlanArchiveStatus,
  startServicePlanArchive,
  updateServicePlanById,
  changePlanUnrealImage,
  previewPlanImageReplacements,
  replacePlanImages,

  getToWorkAvailableCount,
  takePlanToWork,
  getNinjaHasRejectedPlans,
  getNinjaStatisticById,
  getNinjaStatistic3dById,

  getTagsList,
  createTag,
  editTag,
  deleteTag,

  getUsersList,
  getDeletedUsersList,
  changeUserRole,
  changeUserName,
  deleteUser,
  restoreUser,
  changeNinjaStatus,
  changeBetaTesterStatus,
  changeDatasetVoterStatus,
  changeCanGetPlansToWorkStatus,
  changePlansLimit,
  changeReviewerPlansLimit,

  getDesignFiles,
  createDesignFile,
  deleteDesignFile,
  changeDesignFile,
  downloadDesignFile,

  getFurnitureList,
  getFurniture,
  getFurnitureSimpleList,
  getFurnitureTags,
  getFurnitureGroups,
  updateFurnitureGroup,
  deleteFurnitureGroup,
  getFurnitureGroupTags,
  createFurnitureGroupTag,
  updateFurnitureGroupTag,
  deleteFurnitureGroupTag,
  getTagCategories,
  invalidateFurniture,
  getDeletedFurnitureList,
  updateFurnitureParams,
  deleteFurniture,
  restoreFurniture,
  createFurniture,
  createFurnitureTag,
  updateFurnitureTag,
  deleteFurnitureTag,
  createTagCategory,
  updateTagCategory,
  deleteTagCategory,
  deleteFurniturePreview,
  deleteFurnitureTopView,

  getAperturesList,
  getApertureById,
  archiveAperture,
  restoreAperture,
  updateAperture,
  getApertureTags,
  createApertureTag,
  updateApertureTag,
  deleteApertureTag,

  getMoldingsList,
  getMoldingById,
  archiveMolding,
  restoreMolding,
  updateMolding,
  getMoldingTags,
  createMoldingTag,
  updateMoldingTag,
  deleteMoldingTag,
  getStyleMoldings,
  updateStyleMoldings,

  getMaterialsList,
  invalidateMaterials,
  deleteMaterial,
  restoreMaterial,
  getMaterialById,
  updateMaterialPlanStyles,
  updateMaterialRoomTypes,
  getMaterialsTags,
  updateMaterialTags,
  createMaterialTag,

  executeCrmPlanAction,
  reviewCrmPlan,
  reviewPlan,
  deleteCrmPlan,
  cloneCrmPlan,

  getSvgList,
  createSvg,
  invalidateSvg,
  deleteSvgByName,
  archiveSvgByName,
  restoreSvgByName,

  getSvgTags,
  createSvgTag,
  updateSvgTag,
  deleteSvgTag,

  getSvgTypes,
  createSvgType,
  updateSvgType,
  deleteSvgType,

  getSvgFileTags,
  updateSvgFileTags,

  updateSvgIconType,

  changePlanStatus3d,
  getCrmPlan3dPreview,
  getCrmPlan3dResult,
  getCrmPlan3dResultLow,
  getCrmPlan3dResultHigh,
  getCrmPlan3dResultUltraHigh,
  getCrmPlan3dResultUnlit,
  runUnrealGeneration,
  runGltfRender,
  changeCameraNavigationMode,
  getCrmPlanFurniturePlacement,
  changePlanStrategicData,

  getApartmentList,
  getApartmentResponsiblesMatrix,
  updateApartmentResponsiblesBatch,
  updateApartmentResponsibles,
  updateApartment,
  changeApartmentPriority,

  getApartmentsPlansList,

  enableSelectedPlans3d,
  setApartmentPlansPreset,

  getNinjaStatisticPlans,
  getSameCrmPlans,
  getOriginalPlansArchive,
  mergeCrmPlan,
  addBitrixLeadToCrmPlan,
  removeBitrixLeadToCrmPlan,

  getStylesListForFurniture,
  createStyleForFurniture,
  editStyleForFurniture,
  deleteStyleForFurniture,

  getRoomTypesList,
  createRoomType,
  editRoomType,
  archiveRoomType,
  restoreRoomType,
  getNinja2dStatisticPlans,
  getNinja3dStatisticPlans,

  getPaticularNinja2dStatisticFile,
  getPaticularNinja3dStatisticFile,

  createPreset,
  getPresets,
  editPreset,
  changeCrmPlanPreset,
  deletePreset,

  getUnrealEnvironment,
  createUnrealEnvironment,
  editUnrealEnvironment,
  deleteUnrealEnvironment,
  restoreUnrealEnvironment,
  invalidateUnrealEnvironment,
  changeUnrealEnvironment,
  changeManager,
  changeCutter,
  changeReviewer,

  getBrandingsList,
  getMinimalBrandingsList,
  getWidgetBrandingById,
  getBranding,
  createBranding,
  editBranding,
  deleteBranding,
  getPlanBranding,
  getAvailablePlanBrandings,
  attachPlanBranding,
  detachPlanBranding,
  createPlanBranding,
  getServicePlanBranding,
  getAvailableServicePlanBrandings,
  attachServicePlanBranding,
  detachServicePlanBranding,
  createServicePlanBranding,
  getPlansForBranding,

  getChecklists,
  getCrmChecklists,
  getServiceChecklists,
  createChecklist,
  editChecklist,
  deleteChecklist,
  getPlanGuards,
  getPlanGuardById,
  createPlanGuard,
  editPlanGuard,
  deletePlanGuard,
  syncCustomerPlanGuards,
  syncCrmApartmentPlanGuards,
  syncCrmPlanPlanGuards,

  createLanding,
  editLanding,

  getDesignFile,
  createNewDesignFile,

  getCuttingPlansList,
  archiveCuttingPlan,
  unarchiveCuttingPlan,
  startPlanCutting,
  getToWorkPlanCutting,
  showCuttingPlan,
  sendCuttingPlanForProcessing,
  postponeCuttingPlan,
  unpostponeCuttingPlan,
  returnCuttingPlan,
  declineCuttingPlan,
  uploadImagesCuttingPlan,
  submitCuttingPlanForProcessing,

  getPlans,
  getServicePlans,
  reloadFromStep,
  getPlanMaskToWork,
  datasetSelectionPlans,
  datasetSelectionDatasets,
  datasetSelectionPlansReject,
  datasetSelectionPlansAccept,
  datasetSelectionPlansReport,

  resetFromStep,

  getStrategicData,
  getStyleMaterials,
  createStyleMaterial,
  deleteStyleMaterial,

  createRoomStyleItem,
  deleteRoomStyleItem,

  getWidgetVariant,
  createWidgetVariant,
  updateWidgetVariant,
  deleteWidgetVariant,
  updateVariantsPriority,

  getCustomersList,
  createCustomer,
  editCustomer,
  deleteCustomer,

  getWidgetLinkForCrmId,
  getWidgetLinkForPlanId,
  invalidateWidgetCache,
  invalidateCrmWidgetCache,

  findSimilarPlansAi,

  getAdjustmentsByCrmPlanId,

  createLandingTariff,
  updateLandingTariff,
  deleteLandingTariff,
  getWebhooks,
  getWebhookStrategies,
  getWebhook,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  createMattermostLink,
  callbackMattermostLink,
  getUserTelegramId,
  setUserTelegramId,
  removeUserTelegramId,
  getUserSettings,
  updateUserSettings,
  updateUserTelegramNotificationStatus,

  getPlanBatchFormData,
  executePlanBatchAction,
  checkPlanBatchStatus,
  loadPlanBatchHistory,
  getPlanBatchLogDetails,
  getPlanBatchSendPreview,
  getCrmPlanSendStatus,
  sendCrmPlanToCustomer,

  getCrmPlanActivityLogs,
  getServicePlanActivityLogs,

  getCrmPlanRevision,
  getCrmPlanRevisions,
  getServicePlanRevision,
  getServicePlanRevisions,
  updateCrmPlanRevision,
  updateServicePlanRevision,

  updateApartmentBrp,
  getPayrollPeriods,
  getMyPayroll,
  getPayrollEmployees,
  getPayrollSummary,
  getEmployeePayroll,
  setPayrollAmount,
  resetPayrollAmount,
  getPayrollAmountLog,

  getWorkSchedulePeriods,
  getMyWorkScheduleStatus,
  getMyWorkSchedule,
  saveMyWorkSchedule,
  getWorkScheduleEmployees,
  getEmployeeWorkSchedule,
  saveEmployeeWorkSchedule,
  getEmployeeWorkScheduleLog,
  getEmployeeWorkScheduleBrief,
};
